import { useState } from 'react';
import { convertHeicFilesToJpeg } from '../../../utility/convertHeicToJpeg';

/**
 * Manages image upload, preview, and removal logic
 * Encapsulates HEIC conversion and FileList manipulation
 */
export const useImageHandling = () => {
  const [storedImages, setStoredImages] = useState<FileList | null>(null);
  const [prefilledImages, setPrefilledImages] = useState<string[]>([]);

  const handleImageChange = async (files: FileList | null, setError: any, clearErrors: any) => {
    if (files && files.length > 0) {
      let processedFiles = files;
      try {
        processedFiles = await convertHeicFilesToJpeg(files);
      } catch (e) {
        console.warn('HEIC conversion skipped due to error:', e);
      }

      setStoredImages(processedFiles);
      clearErrors('images');
    } else {
      setStoredImages(null);

      if (prefilledImages.length === 0) {
        setError('images', {
          type: 'manual',
          message: 'At least one image is required',
        });
      } else {
        clearErrors('images');
      }
    }
  };

  const handleImageRemove = (index: number, setValue: any, setError: any, clearErrors: any) => {
    if (index < prefilledImages.length) {
      const newPrefilled = prefilledImages.filter((_, i) => i !== index);
      setPrefilledImages(newPrefilled);

      if (newPrefilled.length === 0 && (!storedImages || storedImages.length === 0)) {
        setValue('images', undefined, { shouldValidate: true });
        setError('images', {
          type: 'manual',
          message: 'At least one image is required',
        });
      }
      return;
    }

    const storedIndex = index - prefilledImages.length;
    if (!storedImages) return;

    const dt = new DataTransfer();
    const filesArray = Array.from(storedImages);

    filesArray.forEach((file, i) => {
      if (i !== storedIndex) {
        dt.items.add(file);
      }
    });

    const newFileList = dt.files;

    if (newFileList.length === 0) {
      setStoredImages(null);

      if (prefilledImages.length === 0) {
        setValue('images', undefined, { shouldValidate: true });
        setError('images', {
          type: 'manual',
          message: 'At least one image is required',
        });
      } else {
        setValue('images', undefined);
        clearErrors('images');
      }
    } else {
      setStoredImages(newFileList);
      setValue('images', newFileList as any, { shouldValidate: true });
    }
  };

  return {
    storedImages,
    setStoredImages,
    prefilledImages,
    setPrefilledImages,
    handleImageChange,
    handleImageRemove,
  };
};

export default useImageHandling;
